import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import _ from 'lodash';
import Box from '@mui/material/Box';
import { CommonEmptyDataV1, CommonInvoiceDialogV1, CommonStatusDisplayV1 } from 'gemlux-common-components';
import { pdf } from '@react-pdf/renderer';
import toast from 'react-hot-toast';
import saveAs from 'file-saver';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { IInvoice, IInvoiceMeta, invoiceActions } from '../../redux/invoice/invoiceSlice';
import { SortOrder } from '../../utils/enums/sort-order.enum';
import { Platform } from '../../utils/enums/platform-type.enum';
import { paymentActions } from '../../redux/payment/paymentSlice';
import { InvoiceMetaEnum } from '../../utils/enums/invoice-meta.enum';
import CustomTable, { ColumnProps } from '../../components/V1/CustomTable/CustomTable';
import PaymentAction from '../../components/V1/CommonVisibilityActionButton/PaymentAction';
import Invoice from './InvoicePdf';
import { toastStyles } from '../../utils/styles';

export interface TableTitleprops {
  key: string;
  name: string | number;
  align?: 'left' | 'right' | 'center';
  bold?: boolean;
  padding?: string;
}

export interface TableRowProps {
  rowKey: number;
  finalrow?: boolean;
  value: TableTitleprops[];
}

const columns: ColumnProps[] = [
  {
    key: 'invoiceId',
    name: 'Invoice #',
    enableSort: true,
    align: 'left',
    width: '8%',
    resizable: false,
  },
  { key: 'invoiceDate', name: 'Invoice Date', enableSort: true, align: 'left', width: '8%', resizable: false },
  {
    key: 'subscriptionName',
    name: 'Subscription Plan',
    enableSort: true,
    align: 'left',
    width: '8%',
    resizable: false,
  },
  {
    key: 'amount',
    name: 'Amount',
    enableSort: true,
    align: 'right',
    width: '20%',
    resizable: false,
    headerAlign: 'right',
  },
  { key: 'paidDateAndTime', name: 'Paid Date Time', enableSort: true, align: 'left', width: '15%', resizable: false },
  { key: 'status', name: 'Status', enableSort: true, align: 'left', width: '10%', resizable: false },
  {
    key: 'action',
    name: 'Actions',
    enableSort: false,
    align: 'center',
    width: '3%',
    resizable: false,
    headerAlign: 'center',
  },
];

export default function Payments() {
  const dispatch = useAppDispatch();
  const organizationId = useAppSelector((state: RootState) => state.user.organizationId);
  const {
    componentStatus,
    invoices = [],
    totalInvoicesCount,
    invoiceDetail,
  } = useAppSelector((state: RootState) => state.invoice);

  const stripeAccountId = useAppSelector((state: RootState) => state.auth?.stripeAccountId);
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = searchParams.get('page');
  const rowsPerPageParam = searchParams.get('rows-per-page');
  const sortByParam = searchParams.get('sort-by');
  const sortOrderParam = searchParams.get('sort-order');
  const [page, setPage] = useState<number>(pageParam ? parseInt(pageParam, 10) : 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageParam ? parseInt(rowsPerPageParam, 10) : 10);
  const [sortBy, setSortBy] = useState<string>(sortByParam ?? 'createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>((sortOrderParam as SortOrder) ?? SortOrder.DESC);
  const getInvoicesLoading = Boolean(_.get(componentStatus, 'getInvoicesLoading', false));
  const getInvoiceByPaymentIdLoading = Boolean(_.get(componentStatus, 'getInvoiceByPaymentIdLoading', false));
  const [hasInvoices, setHasInvoices] = useState<boolean>(false);
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState<boolean>(false);
  const [currentPaymentId, setCurrentPaymentId] = useState<string>('');
  const paymentSuccess = useAppSelector((state: RootState) => state.payment.paymentSuccess);

  const rows: TableRowProps[] = [
    {
      rowKey: 1,
      value: [
        {
          key: 'baseCost',
          name: `Base Cost (${invoiceDetail.subscriptionMeta?.name}) `,
          align: 'left',
        },
        {
          key: 'baseCostQuantity',
          name: `${invoiceDetail.subscriptionMeta?.quantity ?? 0}`,
          align: 'right',
        },
        {
          key: 'baseCostUnitPrice',
          name: `$${Number(invoiceDetail.subscriptionMeta?.baseValue ?? 0).toFixed(2)}`,
          align: 'right',
        },
        {
          key: 'price',
          name: `$${(
            (Number(invoiceDetail.subscriptionMeta?.baseValue) ?? 0) *
            (Number(invoiceDetail.subscriptionMeta?.quantity) ?? 0)
          ).toFixed(2)}`,
          align: 'right',
        },
      ],
    },
    {
      rowKey: 2,
      value: [
        {
          key: 'excessUsage',
          name: 'Excess Usage',
          align: 'left',
          bold: true,
        },
      ],
    },
  ];

  const getFilteredRows = (invoiceRows: TableRowProps[]): TableRowProps[] =>
    invoiceRows.filter((row, index) => {
      if (index === 0 || row.finalrow) return true;
      return !invoiceDetail.isCorporate;
    });

  const downloadInvoice = async (paymentId: string) => {
    dispatch(invoiceActions.downloadInvoiceAsync(paymentId));
  };
  const generateInvoicePdf = async () => {
    const invoice = invoices.filter((i) => i.systemId === invoiceDetail.invoiceId)[0];
    if (!invoice) {
      toast.error('Invoice not found.', toastStyles.error);
      return;
    }
    try {
      const invoiceRows = getFilteredRows(rows);
      const blob = await pdf(<Invoice invoiceDetails={invoiceDetail} invoice={invoice} rows={invoiceRows} />).toBlob();
      const pdfName = `${invoice?.systemId}_Invoice.pdf`;
      saveAs(blob, pdfName);
      toast.success('Invoice downloaded successfully.', toastStyles.success);
    } catch (error) {
      toast.error('Failed to generate PDF.', toastStyles.error);
    }
  };
  const triggerInvoiceDownload = () => {
    if (!componentStatus.getInvoiceByPaymentIdLoading) {
      generateInvoicePdf().then(() => {
        dispatch(invoiceActions.clearComponentStatus(['downloadInvoiceLoading']));
      });
    }
  };
  useEffect(() => {
    setSearchParams({
      page: page.toString(),
      'rows-per-page': rowsPerPage.toString(),
      'sort-by': sortBy,
      'sort-order': sortOrder,
    });
  }, [page, rowsPerPage, sortBy, sortOrder]);

  useEffect(() => {
    if (organizationId) {
      dispatch(invoiceActions.getInvoicesAsync({ page, limit: rowsPerPage, sortOrder, organizationId }));
    }
  }, [dispatch, organizationId, page, rowsPerPage, sortBy, sortOrder]);

  useEffect(() => {
    if (totalInvoicesCount > 0 && !hasInvoices) {
      setHasInvoices(true);
    }
  }, [totalInvoicesCount, hasInvoices]);

  useEffect(() => {
    if (currentPaymentId) {
      dispatch(invoiceActions.getInvoiceByPaymentIdAsync(currentPaymentId));
    }
  }, [currentPaymentId]);

  useEffect(() => {
    if (paymentSuccess && organizationId) {
      setOpenInvoiceDialog(false);
      dispatch(invoiceActions.getInvoicesAsync({ page, limit: rowsPerPage, sortOrder, organizationId }));
      dispatch(paymentActions.clearPaymentSuccess());
    }
  }, [paymentSuccess]);

  useEffect(() => {
    if (componentStatus.downloadInvoiceLoading) {
      triggerInvoiceDownload();
    }
  }, [componentStatus, invoiceDetail]);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (rowsCount: number) => {
    const totalPages = Math.ceil(totalInvoicesCount / rowsCount);
    const newPage = Math.min(page, totalPages);
    setPage(newPage);
    setRowsPerPage(rowsCount);
  };

  const handleOpen = (id: string) => {
    setCurrentPaymentId(id);
    setOpenInvoiceDialog(true);
  };

  const handleClose = () => {
    setOpenInvoiceDialog(false);
  };

  const handleRetryPayment = () => {
    if (!stripeAccountId || !invoiceDetail.total || !currentPaymentId) return;
    dispatch(
      paymentActions.retryPaymentAsync({ stripeAccountId, amount: invoiceDetail.total, paymentId: currentPaymentId }),
    );
  };

  const getMetaName = (key: InvoiceMetaEnum): string | number => {
    switch (key) {
      case InvoiceMetaEnum.IMAGE_CAPTURING_KRISTA_KROMA:
        return 'Gem Images & Profile [KRISTA™/KROMA™]';
      case InvoiceMetaEnum.CAPTURE_VIDEO:
        return 'Gem3D™ Video';
      case InvoiceMetaEnum.GEM_PASSPORT:
        return 'GemPASSPORT™';
      case InvoiceMetaEnum.GEM_PASSPORT_LITE:
        return 'GemPASSPORT™';
      default:
        return key;
    }
  };
  const arrangeInvoiceMeta = (invoiceMeta: IInvoiceMeta[]): TableRowProps[] => {
    const order = [
      InvoiceMetaEnum.IMAGE_CAPTURING_KRISTA_KROMA,
      InvoiceMetaEnum.CAPTURE_VIDEO,
      InvoiceMetaEnum.GEM_PASSPORT,
      InvoiceMetaEnum.GEM_PASSPORT_LITE,
    ];

    return order.map((key, index) => {
      const metaItem = invoiceMeta.find((meta) => meta.key === key);
      return {
        rowKey: index + 3,
        value: [
          {
            key: key.toLowerCase(),
            name: getMetaName(key),
            align: 'left',
            padding: '0 0 0 30px',
          },
          {
            key: `${key.toLowerCase()}BaseCostQuantity`,
            name: `${metaItem?.quantity ?? 0}`,
            align: 'right',
          },
          {
            key: `${key.toLowerCase()}UnitPrice`,
            name: `$${Number(metaItem?.amount ?? 0).toFixed(2)}`,
            align: 'right',
          },
          {
            key: 'price',
            name: `$${(Number(metaItem?.quantity ?? 0) * parseFloat(metaItem?.amount ?? '0')).toFixed(2)}`,
            align: 'right',
          },
        ],
      };
    });
  };
  rows.push(...arrangeInvoiceMeta(invoiceDetail.invoiceMeta), {
    rowKey: 7,
    finalrow: true,
    value: [
      {
        key: 'total',
        name: 'Total',
        align: 'right',
      },
      {
        key: 'amount',
        name: 'Total',
        align: 'right',
      },
    ],
  });

  const totalRow = rows.find((row) => row.rowKey === 7);
  if (totalRow) {
    const amountItem = totalRow.value.find((item: { key: string }) => item.key === 'amount');
    if (amountItem) {
      if (invoiceDetail.isCorporate) {
        amountItem.name = `$${invoiceDetail.total?.toFixed(2)}`;
      } else {
        amountItem.name = `$${(invoiceDetail.total || 0).toFixed(2)}`;
      }
    }
  }

  const filteredRows: TableRowProps[] = rows.filter((row, index) => {
    if (index === 0 || row.finalrow) return true;

    return !invoiceDetail.isCorporate;
  });

  const getFormattedInvoices = (invoiceList: IInvoice[]) =>
    invoiceList.map((invoice: IInvoice) => ({
      invoiceId: invoice.systemId,
      invoiceDate: invoice.invoiceDate.split('T')[0],
      subscriptionName: invoice.subscriptionPlan.split(' ')[0],
      amount: `$${Number(invoice.total ?? 0).toFixed(2)}`,
      paidDateAndTime: invoice.paidDateAndTime
        ? (dayjs(invoice.paidDateAndTime).format('DD/MM/YYYY HH:mm') ?? 'N/A')
        : 'N/A',
      status: <CommonStatusDisplayV1 status={invoice.status} />,
      action: (
        <PaymentAction
          handleOpen={() => handleOpen(invoice.paymentId)}
          handleDownload={() => downloadInvoice(invoice.paymentId)}
        />
      ),
    }));
  return (
    <Box sx={{ width: '100%' }}>
      {invoices?.length === 0 && !getInvoicesLoading && !hasInvoices ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CommonEmptyDataV1 title="No Invoices Found!" description="Sorry, we couldn’t find any invoices." />
        </Box>
      ) : (
        <Box sx={{ width: '100%', typography: 'body1', mt: 1 }}>
          <CustomTable
            isLoading={getInvoicesLoading}
            totalDataRows={totalInvoicesCount}
            enablePagination
            selectedPage={page}
            enablePagePerRows
            rowsPerList={[10, 15, 20]}
            selectedRowsPerPage={rowsPerPage}
            sortBy={sortBy}
            sortOrder={sortOrder}
            columns={columns}
            rows={getFormattedInvoices(invoices)}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleSortByChange={setSortBy}
            handleSortOrderChange={setSortOrder}
            tableType="Invoices"
          />
          <CommonInvoiceDialogV1
            handleDownloadInvoice={triggerInvoiceDownload}
            isLoading={getInvoiceByPaymentIdLoading}
            handleClose={handleClose}
            open={openInvoiceDialog}
            invoiceDetail={invoiceDetail}
            rows={filteredRows}
            platform={Platform.SAAS}
            handleRetryPayment={handleRetryPayment}
          />
        </Box>
      )}
    </Box>
  );
}
